<template>
  <div
    class="flex h-[50px] items-center justify-between bg-[color:var(--color-header-top-bg)] text-sm text-[color:var(--color-header-top-text)]"
  >
    <div class="container mx-auto flex w-full max-w-screen-xl justify-between">
      <div class="flex items-center">
        <template v-for="(usp, index) in $cfg.uspsTopbar" :key="index">
          <div class="mr-2.5 flex items-center">
            <VcIcon name="checkmark" class="mr-2 text-white" size="xs" lazy />
            <p class="text-sm">{{ usp.label }}</p>
          </div>
        </template>
      </div>

      <div ref="loginMenu" class="relative flex items-center">
        <div v-if="!operator && numberOfOrganizations === 1" class="flex items-center space-x-4">
          <template v-for="(item, index) in topHeaderList" :key="index">
            <a
              v-if="isExternalLink(item.route)"
              class="catalog-menu-child-link"
              :href="item.route as string"
              target="_blank"
            >
              {{ item.title }}
            </a>
            <router-link v-else class="catalog-menu-child-link" :to="item.route ?? '#'">
              {{ item.title }}
            </router-link>
          </template>
        </div>

        <div v-if="operator" class="ml-4 flex">
          <div class="mr-1 font-bold text-white">
            {{ operator.contact?.fullName || operator.userName }}
          </div>
          <span class="font-bold text-white">{{ $t("shared.layout.header.top_header.logged_in_as") }}</span>
        </div>

        <button
          v-if="operator || numberOfOrganizations > 1"
          class="ml-1 flex cursor-pointer items-center py-1 text-white hover:text-[color:var(--color-header-top-link)]"
          @click="loginMenuVisible = !loginMenuVisible"
        >
          <span
            v-if="operator && numberOfOrganizations > 1"
            class="font-bold text-[color:var(--color-secondary-500)]"
            >{{ organization?.name }}</span
          >

          <span v-else-if="operator" class="font-bold text-[color:var(--color-secondary-500)]">{{
            user.contact?.fullName || user.userName
          }}</span>

          <span v-else class="font-bold text-white">{{ organization?.name }}</span>

          <VcIcon
            class="ms-1.5 text-[color:var(--color-secondary-500)]"
            size="xs"
            :name="loginMenuVisible ? 'chevron-up' : 'chevron-down'"
          />
        </button>

        <div
          v-if="loginMenuVisible && (operator || numberOfOrganizations > 1)"
          class="absolute right-0 top-full z-10 flex w-60 flex-col space-y-3 rounded-md bg-white px-3 py-4 text-black shadow-md"
        >
          <div class="flex items-center justify-between">
            <!-- Innovadis custom: use /account so account route redirect chooses the first visible destination -->
            <router-link
              to="/account"
              class="flex items-center hover:text-[color:var(--color-primary)]"
              @click="loginMenuVisible = false"
            >
              <VcIcon class="text-[--color-primary]" name="user-circle" />

              <span class="ml-2">{{ user.contact?.fullName }}</span>
            </router-link>

            <button
              type="button"
              :title="$t('shared.layout.header.link_logout')"
              class="border-gray-200 ml-4 size-6 rounded border text-neutral-400 shadow hover:bg-neutral-200"
              @click="signOut"
            >
              <VcIcon class="mt-0.5" name="logout" :size="16" />
            </button>
          </div>

          <div class="flex w-full" v-if="numberOfOrganizations > 1">
            <router-link
              to="/account/afzetpunt"
              class="flex w-full items-center justify-between hover:text-[color:var(--color-primary)]"
              @click="loginMenuVisible = false"
            >
              <span class="ml-2">{{ $t("shared.account.sign_in_form.impersonate.switch_drop_off_point") }}</span>

              <VcIcon name="truck" class="text-neutral-400" />
            </router-link>
          </div>

          <div
            v-if="checkPermissions(VirtoPermissions.LoginOnBehalf)"
            class="flex w-full"
            @click="loginMenuVisible = false"
          >
            <router-link
              :to="{ name: 'SignInAdministrator' }"
              class="flex w-full items-center justify-between hover:text-[color:var(--color-primary)]"
            >
              <span class="ml-2">{{ $t("shared.account.sign_in_form.impersonate.switch_user") }}</span>
              <button
                type="button"
                :title="$t('shared.layout.header.link_logout')"
                class="border-gray-200 ml-4 size-6 rounded border text-neutral-400 shadow hover:bg-neutral-200"
              >
                <VcIcon class="mt-0.5" name="rotate" :size="16" />
              </button>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-unused-vars */
import { onClickOutside } from "@vueuse/core";
import { ref, computed } from "vue";
import { VirtoPermissions } from "@/core/enums";
import { getLinkAttr } from "@/core/utilities";
import { useSignMeOut, useUser } from "@/shared/account";
import type { RouteLocationRaw } from "vue-router";
import { useNavigations } from "@/core/composables";

const { user, operator, checkPermissions, organization } = useUser();
const { signMeOut } = useSignMeOut();
const { topHeaderList } = useNavigations();

const numberOfOrganizations = computed<number>(() => user.value.contact?.organizations?.totalCount || 1);

const loginMenu = ref(null);
const loginMenuVisible = ref(false);

async function signOut() {
  await signMeOut();
  location.href = "/";
}

onClickOutside(loginMenu, () => {
  loginMenuVisible.value = false;
});

function isExternalLink(link?: RouteLocationRaw) {
  return "externalLink" in getLinkAttr(link);
}
</script>
